/**
 * Free Gift Cart Logic
 *
 * - Loads rules from /assets/free-gift-config.json
 * - Watches for cart updates (add-to-cart buttons, cart quantity changes)
 * - Fetches the cart via /cart.js
 * - Automatically adds/removes gift items via /cart/add.js and /cart/change.js
 * - Compatible with Online Store 2.0 themes (e.g. Dawn)
 *
 * This script is safe to include globally; it only acts when rules are enabled.
 */

(() => {
  const FreeGiftManager = {
    config: null,
    initialised: false,
    isProcessing: false,
    checkCartDebounced: null,

    /**
     * Initialise script (called on DOMContentLoaded & section loads).
     */
    async init() {
      if (this.initialised) return;

      if (typeof window.FREE_GIFT_ENABLED !== 'undefined' && !window.FREE_GIFT_ENABLED) {
        return;
      }

      try {
        this.config = await this.loadConfig();
        if (!this.config || !Array.isArray(this.config.rules) || this.config.rules.length === 0) {
          console.warn('[FreeGift] No rules configured.');
          return;
        }

        this.bindCartListeners();
        this.checkCartDebounced(); // Initial check

        this.initialised = true;
        console.info('[FreeGift] Initialised with rules:', this.config.rules);
      } catch (error) {
        console.error('[FreeGift] Initialisation error:', error);
      }
    },

    /**
     * Load config JSON from theme assets.
     */
    async loadConfig() {
      const url = window.FREE_GIFT_CONFIG_URL;
      if (!url) {
        console.error('[FreeGift] FREE_GIFT_CONFIG_URL not set.');
        return null;
      }

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store'
      });

      if (!response.ok) {
        console.error('[FreeGift] Failed to load config:', response.status, response.statusText);
        return null;
      }

      return response.json();
    },

    /**
     * Setup listeners that indicate cart changes.
     * Works for both cart pages and cart drawers in a generic way.
     */
    bindCartListeners() {
      // Debounced checker to avoid spamming /cart.js
      this.checkCartDebounced = this.debounce(() => {
        this.checkCart().catch((e) => console.error('[FreeGift] checkCart error:', e));
      }, 700);

      // Initial triggers
      document.addEventListener('DOMContentLoaded', () => this.checkCartDebounced());
      window.addEventListener('pageshow', () => this.checkCartDebounced());

      // Shopify section re-render (e.g., cart drawer)
      document.addEventListener('shopify:section:load', () => this.checkCartDebounced());

      // Catch add-to-cart button clicks (product pages, collection cards, etc.)
      document.addEventListener('click', (event) => {
        const target = event.target;
        if (!target) return;

        const button = target.closest(
          'button[name="add"], button[type="submit"][form], button[data-add-to-cart], form[action*="/cart/add"] button[type="submit"]'
        );

        if (button) {
          // After Shopify handles the add, we re-check the cart.
          setTimeout(() => this.checkCartDebounced(), 1000);
        }
      });

      // Listen for quantity changes in cart forms (cart page & some drawers)
      document.addEventListener('change', (event) => {
        const target = event.target;
        if (!target) return;

        const quantityInput = target.closest('input[name^="updates["], input[data-cart-quantity], input[name="quantity"]');
        if (quantityInput) {
          this.checkCartDebounced();
        }
      });

      // Optional: if theme triggers a custom cart refresh event, hook into it.
      document.addEventListener('cart:refresh', () => this.checkCartDebounced());
    },

    /**
     * Debounce helper.
     */
    debounce(fn, delay) {
      let timer = null;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), delay);
      };
    },

    /**
     * Main logic: inspect cart and apply rules.
     */
    async checkCart() {
      if (this.isProcessing) return;
      this.isProcessing = true;

      try {
        const cart = await this.getCart();
        if (!cart || !Array.isArray(cart.items)) {
          console.warn('[FreeGift] Cart data invalid.');
          return;
        }

        for (const rawRule of this.config.rules) {
          const rule = this.normaliseRule(rawRule);
          if (!rule.enabled) continue;

          const giftLineItem = this.getGiftLineItem(cart, rule);

          const qualifiesBySpend = cart.total_price >= rule.min_spend;
          const qualifiesByProduct = this.hasTriggerProducts(cart, rule);

          const shouldHaveGift = qualifiesBySpend && qualifiesByProduct;

          // If cart should have gift
          if (shouldHaveGift) {
            const desiredQty = rule.quantity > 0 ? rule.quantity : 1;
            const currentQty = giftLineItem ? giftLineItem.quantity : 0;

            if (currentQty < desiredQty) {
              // Ensure stock if required
              if (rule.only_if_in_stock) {
                const inStock = await this.isVariantInStock(rule.free_gift_variant_id);
                if (!inStock) {
                  console.warn('[FreeGift] Gift variant out of stock, skipping add.');
                  continue;
                }
              }

              await this.addFreeGift(rule, currentQty, desiredQty);
            } else if (currentQty > desiredQty) {
              // Too many gifts for this rule - reduce down to desired quantity
              if (giftLineItem) {
                await this.updateGiftQuantity(giftLineItem, desiredQty);
              }
            }
          } else {
            // Cart no longer qualifies
            if (giftLineItem && rule.remove_if_below_threshold) {
              await this.removeFreeGift(giftLineItem);
            }
          }
        }
      } catch (error) {
        console.error('[FreeGift] Error in checkCart:', error);
      } finally {
        this.isProcessing = false;
      }
    },

    /**
     * Get cart via Ajax API.
     */
    async getCart() {
      const response = await fetch('/cart.js', {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json'
        },
        cache: 'no-store'
      });

      if (!response.ok) {
        console.error('[FreeGift] Failed to fetch cart:', response.status, response.statusText);
        return null;
      }

      return response.json();
    },

    /**
     * Normalise rule values (string/number etc).
     */
    normaliseRule(rule) {
      return {
        id: rule.id || 'rule-' + (rule.free_gift_variant_id || 'unknown'),
        enabled: Boolean(rule.enabled),
        free_gift_variant_id: Number(rule.free_gift_variant_id),
        quantity: Number(rule.quantity || 1),
        min_spend: Number(rule.min_spend || 0),
        trigger_product_ids: Array.isArray(rule.trigger_product_ids)
          ? rule.trigger_product_ids.map((id) => Number(id))
          : [],
        required_trigger_quantity: Number(rule.required_trigger_quantity || 1),
        only_if_in_stock: rule.only_if_in_stock !== false,
        remove_if_below_threshold: rule.remove_if_below_threshold !== false
      };
    },

    /**
     * Does the cart contain any of the trigger products (if specified)?
     * If no trigger_product_ids are set, this returns true.
     */
      hasTriggerProducts(cart, rule) {
  // No trigger products configured
  if (!rule.trigger_product_ids || rule.trigger_product_ids.length === 0) {
    return true;
  }

  // Default required quantity = 1
  const requiredQty = Number(rule.required_trigger_quantity || 1);

  // Count total quantity of qualifying products
  let matchingQty = 0;

  cart.items.forEach((item) => {
    if (rule.trigger_product_ids.includes(Number(item.product_id))) {
      matchingQty += Number(item.quantity || 0);
    }
  });

  return matchingQty >= requiredQty;
},

    /**
     * Find the existing line item for the free gift (if present).
     */
    getGiftLineItem(cart, rule) {
      if (!rule.free_gift_variant_id) return null;

      return cart.items.find((item) => Number(item.variant_id) === Number(rule.free_gift_variant_id)) || null;
    },

      /**
     * Check variant stock via /variants/{id}.js
     *
     * IMPORTANT:
     * - Storefront /variants/{id}.js does NOT always include inventory_quantity or inventory_policy.
     * - It DOES include `available`, which already accounts for inventory + policy.
     * - So we primarily trust `variant.available`, and only fall back to quantity if present.
     */
    async isVariantInStock(variantId) {
      if (!variantId) return false;

      try {
        const response = await fetch(`/variants/${variantId}.js`, {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store'
        });

        if (!response.ok) {
          console.warn('[FreeGift] Could not fetch variant data:', response.status);
          return false;
        }

        const variant = await response.json();

        // Preferred: rely on Shopify's `available` flag
        if (typeof variant.available === 'boolean') {
          return variant.available;
        }

        // Fallback: if inventory fields happen to be present, use them
        if (typeof variant.inventory_policy !== 'undefined') {
          if (variant.inventory_policy === 'continue') {
            return true;
          }

          if (typeof variant.inventory_quantity !== 'undefined') {
            return Number(variant.inventory_quantity || 0) > 0;
          }
        }

        // If we can't determine, be conservative and assume out of stock
        return false;
      } catch (error) {
        console.error('[FreeGift] Error checking variant stock:', error);
        return false;
      }
    },

    /**
     * Add gift quantity to reach desiredQty.
     */
    async addFreeGift(rule, currentQty, desiredQty) {
      const qtyToAdd = desiredQty - currentQty;
      if (qtyToAdd <= 0) return;

      console.info('[FreeGift] Adding gift', rule.free_gift_variant_id, 'qty', qtyToAdd);

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: [
              {
                id: rule.free_gift_variant_id,
                quantity: qtyToAdd
              }
            ]
          })
        });

        if (!response.ok) {
          console.error('[FreeGift] Failed to add free gift:', response.status, response.statusText);
          return;
        }

        await response.json(); // Not strictly needed but consumes body
        this.refreshCartUI();
      } catch (error) {
        console.error('[FreeGift] Error adding free gift:', error);
      }
    },

    /**
     * Update existing gift line quantity (e.g. from 3 down to 1).
     */
    async updateGiftQuantity(lineItem, newQty) {
      if (!lineItem || !lineItem.key) return;

      console.info('[FreeGift] Updating gift quantity to', newQty);

      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: lineItem.key,
            quantity: newQty
          })
        });

        if (!response.ok) {
          console.error('[FreeGift] Failed to update free gift quantity:', response.status);
          return;
        }

        await response.json();
        this.refreshCartUI();
      } catch (error) {
        console.error('[FreeGift] Error updating free gift quantity:', error);
      }
    },

    /**
     * Remove gift line item completely.
     */
    async removeFreeGift(lineItem) {
      if (!lineItem || !lineItem.key) return;

      console.info('[FreeGift] Removing free gift line item.');

      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: lineItem.key,
            quantity: 0
          })
        });

        if (!response.ok) {
          console.error('[FreeGift] Failed to remove free gift:', response.status, response.statusText);
          return;
        }

        await response.json();
        this.refreshCartUI();
      } catch (error) {
        console.error('[FreeGift] Error removing free gift:', error);
      }
    },

    /**
     * Refresh cart UI:
     * - Tries to trigger a generic 'cart:refresh' event for themes that support it
     * - If on /cart page, falls back to a full reload
     */
    refreshCartUI() {
      try {
        const event = new CustomEvent('cart:refresh', { bubbles: true });
        document.dispatchEvent(event);

        const drawer = document.querySelector('cart-drawer, [data-cart-drawer]');
        if (drawer) {
          drawer.dispatchEvent(event);
        }

        if (window.location.pathname.indexOf('/cart') === 0) {
          window.location.reload();
        }
      } catch (error) {
        console.error('[FreeGift] Error refreshing cart UI; reloading page as fallback.', error);
        window.location.reload();
      }
    }
  };

  // Kick things off
  document.addEventListener('DOMContentLoaded', () => FreeGiftManager.init());
})();